/**
 * Give somebody a login, and compose the message that tells them where to use it.
 *
 * WHY THIS DRAWS A BLOCK OF TEXT TO COPY RATHER THAN JUST SAYING "INVITED"
 *
 * Cognito does email the new account its temporary password, so the credentials
 * arrive on their own. Two things are wrong with that email and neither is fixable
 * from this repo:
 *
 *   1. It contains no link. Username and password, nothing about where to sign in.
 *   2. It is branded "QWealth Marketing Compliance Review", because the pool is
 *      shared with that tool and the invite template is per-POOL, not per-app.
 *
 * Rebranding it would change what the compliance tool's invitees receive, and the
 * template may well be managed by that repo's CDK, in which case editing it from here
 * gets silently reverted on their next deploy. So the gap is closed by a human: the
 * admin sends the URL, and the copy block says the mis-branded subject line is
 * expected, which is the sentence that stops the new person deciding it is phishing.
 *
 * The message is deliberately shown even when nothing happened - see below.
 */

import { useState } from 'react';
import styled from 'styled-components';

import { describeError, invitePerson } from '../services/api';
import { palette, radius } from '../styles/theme';
import { ErrorText, Hint, Input, Label, PrimaryButton, SecondaryButton } from '../styles/ui';
import type { InviteResult } from '../types';

const Form = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 260px;
  flex: 1;
`;

const Result = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: ${radius.lg};
  background: rgba(224, 33, 138, 0.05);
  border: 1px solid rgba(224, 33, 138, 0.18);
`;

const Summary = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${palette.ink};
`;

/*
  A readonly textarea rather than a <pre>. The point of this block is that it leaves
  here and lands in Teams, so it has to survive a manual select-all on the browsers
  where the clipboard API is unavailable - and a textarea is the one element that
  makes that a drag-free triple-click.
*/
const Message = styled.textarea`
  width: 100%;
  min-height: 132px;
  resize: vertical;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  padding: 10px;
  border-radius: ${radius.sm};
  border: 1px solid rgba(224, 33, 138, 0.25);
  background: #ffffff;
  color: ${palette.ink};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/** Where the app actually lives, for the message body. */
const APP_URL = window.location.origin;

/**
 * What to send them.
 *
 * Names the wrong-looking subject line on purpose. An unexplained email about a
 * compliance tool, arriving because somebody added you to a roadmap, is exactly the
 * shape of a phishing attempt - and the correct response to a suspicious credential
 * email is to ignore it, so an invite that does not pre-empt that gets ignored by the
 * most security-conscious people on the team.
 */
export function inviteMessage(result: InviteResult, appUrl: string = APP_URL): string {
  const credentials = result.account_created
    ? [
        'You will get a separate email with a temporary password.',
        'Its subject line says "QWealth Marketing Compliance Review" -',
        "that is expected, it is the same sign-in for both tools.",
      ].join('\n')
    : 'Sign in with your existing QWealth account - the same one you\nuse for the compliance tool. No new password.';

  return [
    "You've been added to the Planning Roadmap:",
    appUrl,
    '',
    credentials,
    '',
    'First time in, it will ask you to fill in your name, what you do,',
    'and which skills you have. That takes a minute and is what puts',
    'you on the team list so work can be assigned to you.',
  ].join('\n');
}

/** The one-line answer to "what just happened", which is not always "invited". */
function summarise(result: InviteResult): string {
  if (result.onboarded) {
    return `${result.email} already has an account and is already on the roster. Nothing changed.`;
  }
  if (!result.account_created && !result.group_added) {
    return `${result.email} already had access. They still need to fill in their roster entry.`;
  }
  if (!result.account_created) {
    return `${result.email} already had an account; added to the planning group.`;
  }
  return `Invited ${result.email}. Cognito has emailed them a temporary password.`;
}

export default function InvitePanel({ onInvited }: { onInvited?: () => void }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InviteResult | null>(null);
  const [copied, setCopied] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const address = email.trim();
    if (!address) {
      return;
    }
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const invited = await invitePerson(address);
      setResult(invited);
      setEmail('');
      onInvited?.();
    } catch (err) {
      setError(describeError(err));
      setResult(null);
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!result) {
      return;
    }
    try {
      await navigator.clipboard.writeText(inviteMessage(result));
      setCopied(true);
    } catch {
      // Clipboard access is refused over plain http and in some locked-down
      // profiles. The textarea is right there and selectable, so this is a missing
      // convenience rather than a failure worth an error banner.
      setCopied(false);
    }
  }

  return (
    <div>
      <Form onSubmit={submit}>
        <Field>
          <Label htmlFor="invite-email">Email address</Label>
          <Input
            id="invite-email"
            type="email"
            required
            placeholder="somebody@qwealth.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <PrimaryButton type="submit" disabled={busy}>
          {busy ? 'Inviting…' : 'Invite'}
        </PrimaryButton>
      </Form>

      <Hint>
        Creates a sign-in and adds it to the planning group. It does not add them to the roster —
        they fill that in themselves the first time they sign in.
      </Hint>

      {error ? <ErrorText role="alert">{error}</ErrorText> : null}

      {result ? (
        <Result>
          <Summary>{summarise(result)}</Summary>
          {result.onboarded ? null : (
            <>
              <Label htmlFor="invite-message">Send them this — it has the link</Label>
              <Message id="invite-message" readOnly value={inviteMessage(result)} />
              <Row>
                <SecondaryButton type="button" onClick={copy}>
                  {copied ? 'Copied' : 'Copy'}
                </SecondaryButton>
                <Hint>Cognito&rsquo;s own email has the password but no link.</Hint>
              </Row>
            </>
          )}
        </Result>
      ) : null}
    </div>
  );
}
